import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import interviewService from "../../services/interviewService";
import { getErrorMessage } from "../../utils/errorHandler";
import { 
  ChevronLeft, ChevronRight, Save, CheckCircle, XCircle, 
  Clock, AlertCircle, User, FileText, Star 
} from "lucide-react";
import "../../styles/recruitment.css";

const InterviewMode = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { interviewId } = useParams();
  
  const [interview, setInterview] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scores, setScores] = useState({});
  const [comments, setComments] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    loadData();
  }, [interviewId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load interview details
      const interviewData = await interviewService.getById(interviewId);
      setInterview(interviewData);
      
      // Load questions
      const questionsData = await interviewService.getQuestions(interviewId);
      setQuestions(questionsData);
      
      // Initialize scores and comments
      const initialScores = {};
      const initialComments = {};
      questionsData.forEach(q => {
        initialScores[q.id] = 0;
        initialComments[q.id] = "";
      });
      setScores(initialScores);
      setComments(initialComments);
      
      setError("");
    } catch (err) {
      console.error("Error loading data:", err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (questionId, value) => {
    const question = questions.find(q => q.id === questionId);
    const maxScore = question?.max_score || 10;
    const clampedValue = Math.min(Math.max(0, value), maxScore);
    setScores(prev => ({ ...prev, [questionId]: clampedValue }));
  };

  const handleCommentChange = (questionId, value) => {
    setComments(prev => ({ ...prev, [questionId]: value }));
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleSaveEvaluation = async () => {
    try {
      setSaving(true);
      setError("");
      
      const evaluationPayload = {
        interview_id: parseInt(interviewId),
        scores: questions.map(q => ({
          interview_id: parseInt(interviewId),
          question_id: q.id,
          score: scores[q.id] || 0,
          max_score: q.max_score,
          comment: comments[q.id] || null,
          interviewer_id: user?.employee_id || null
        }))
      };
      
      await interviewService.evaluate(interviewId, evaluationPayload);
      setSuccess("è‰valuation sauvegardée avec succès !");
      setCompleted(true);
      
      setTimeout(() => {
        navigate("/rh/applications");
      }, 3000);
    } catch (err) {
      console.error("Error saving evaluation:", err);
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      "TECHNICAL": "Technique",
      "BEHAVIORAL": "Comportemental",
      "EXPERIENCE": "Expérience",
      "PROJECT": "Projet",
      "JOB_SPECIFIC": "Spécifique à l'offre",
      "SKILL_VERIFICATION": "Vérification"
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      "TECHNICAL": "#3b82f6",
      "BEHAVIORAL": "#10b981",
      "EXPERIENCE": "#f59e0b",
      "PROJECT": "#8b5cf6",
      "JOB_SPECIFIC": "#ec4899",
      "SKILL_VERIFICATION": "#6366f1"
    };
    return colors[category] || "#6b7280";
  };

  const getDifficultyLabel = (difficulty) => {
    const labels = {
      "EASY": "Facile",
      "MEDIUM": "Moyen",
      "HARD": "Difficile"
    };
    return labels[difficulty] || difficulty;
  };

  const getScorePercentage = (questionId) => {
    const question = questions.find(q => q.id === questionId);
    const score = scores[questionId] || 0;
    const maxScore = question?.max_score || 10;
    return (score / maxScore) * 100;
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return "#10b981";
    if (percentage >= 50) return "#f59e0b";
    return "#ef4444";
  };

  const calculateProgress = () => {
    if (questions.length === 0) return 0;
    const answered = Object.keys(scores).filter(id => scores[id] > 0).length;
    return (answered / questions.length) * 100;
  };

  if (loading) {
    return (
      <div style={{ padding: "24px" }}>
        <div className="loading-spinner">Chargement...</div>
      </div>
    );
  }

  if (!interview || questions.length === 0) {
    return (
      <div style={{ padding: "24px" }}>
        <div className="card-inner-box">
          <h3>Entretien non trouvé</h3>
          <p>Cet entretien n'existe pas ou n'a pas de questions associées.</p>
          <button className="btn btn-secondary" onClick={() => navigate("/rh/applications")}>
            Retour aux candidatures
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = calculateProgress();

  return (
    <div style={{ padding: "24px" }}>
      <div className="page-header">
        <h1>Mode Entretien</h1>
        <p className="page-subtitle">
          {interview.candidate_name} - {interview.job_offer_title}
        </p>
      </div>

      {error && (
        <div className="alert alert-danger">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <CheckCircle size={16} />
          {success}
        </div>
      )}

      {/* Progress Bar */}
      <div className="card-inner-box">
        <div className="card-box-header">
          <h3>Progression</h3>
          <span>{Math.round(progress)}% complété</span>
        </div>
        <div style={{ height: "8px", background: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
          <div 
            style={{ 
              height: "100%", 
              width: `${progress}%`, 
              background: "#10b981", 
              transition: "width 0.3s ease" 
            }} 
          />
        </div>
        <div style={{ marginTop: "12px", display: "flex", justifyContent: "space-between", fontSize: "0.875rem", color: "#64748b" }}>
          <span>Question {currentIndex + 1} sur {questions.length}</span>
          <span>{Object.keys(scores).filter(id => scores[id] > 0).length} notées</span>
        </div>
      </div>

      {/* Question Card */}
      <div className="card-inner-box" style={{ marginTop: "20px" }}>
        <div className="card-box-header">
          <div>
            <h3>Question {currentIndex + 1}</h3>
            <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
              <span 
                className="badge" 
                style={{ backgroundColor: getCategoryColor(currentQuestion.category) + "20", color: getCategoryColor(currentQuestion.category) }}
              >
                {getCategoryLabel(currentQuestion.category)}
              </span>
              {currentQuestion.skill && (
                <span className="badge" style={{ backgroundColor: "#f1f5f9", color: "#475569" }}>
                  {currentQuestion.skill}
                </span>
              )}
              <span className="badge" style={{ backgroundColor: "#fef3c7", color: "#92400e" }}>
                {getDifficultyLabel(currentQuestion.difficulty)}
              </span>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "0.875rem", color: "#64748b" }}>Note maximale</div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1e293b" }}>
              {currentQuestion.max_score}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <h4 style={{ fontSize: "1.125rem", fontWeight: "600", color: "#1e293b", marginBottom: "12px" }}>
            {currentQuestion.question_text}
          </h4>
          
          {currentQuestion.rationale && (
            <div style={{ padding: "12px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "6px", marginBottom: "12px" }}>
              <label style={{ fontSize: "0.75rem", fontWeight: "600", color: "#059669", textTransform: "uppercase" }}>
                Pourquoi cette question
              </label>
              <p style={{ margin: "4px 0 0", fontSize: "0.875rem", color: "#475569" }}>
                {currentQuestion.rationale}
              </p>
            </div>
          )}

          {currentQuestion.expected_elements && (
            <div style={{ padding: "12px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "6px" }}>
              <label style={{ fontSize: "0.75rem", fontWeight: "600", color: "#1d4ed8", textTransform: "uppercase" }}>
                è‰léments attendus
              </label>
              <p style={{ margin: "4px 0 0", fontSize: "0.875rem", color: "#475569" }}>
                {currentQuestion.expected_elements}
              </p>
            </div>
          )}
        </div>

        {/* Scoring Section */}
        <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "20px" }}>
          <div className="form-row">
            <div className="form-group">
              <label>Note attribuée (0-{currentQuestion.max_score})</label>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <input
                  type="range"
                  min="0"
                  max={currentQuestion.max_score}
                  step="0.5"
                  value={scores[currentQuestion.id] || 0}
                  onChange={(e) => handleScoreChange(currentQuestion.id, parseFloat(e.target.value))}
                  style={{ flex: 1 }}
                />
                <div style={{ 
                  minWidth: "60px", 
                  textAlign: "center", 
                  padding: "8px 12px", 
                  background: "#f8fafc", 
                  border: "1px solid #e2e8f0", 
                  borderRadius: "6px",
                  fontWeight: "600",
                  color: getScoreColor(getScorePercentage(currentQuestion.id))
                }}>
                  {scores[currentQuestion.id] || 0}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Pourcentage</label>
              <div style={{ 
                padding: "8px 12px", 
                background: "#f8fafc", 
                border: "1px solid #e2e8f0", 
                borderRadius: "6px",
                fontWeight: "600",
                color: getScoreColor(getScorePercentage(currentQuestion.id))
              }}>
                {getScorePercentage(currentQuestion.id).toFixed(0)}%
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label>Commentaire (optionnel)</label>
              <textarea
                value={comments[currentQuestion.id] || ""}
                onChange={(e) => handleCommentChange(currentQuestion.id, e.target.value)}
                placeholder="Ajoutez vos observations sur la réponse du candidat..."
                rows={3}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="card-inner-box" style={{ marginTop: "20px" }}>
        <div className="form-actions" style={{ justifyContent: "space-between" }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
          >
            <ChevronLeft size={18} />
            Précédent
          </button>

          <div style={{ display: "flex", gap: "8px" }}>
            {currentIndex < questions.length - 1 ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleNext}
              >
                Suivant
                <ChevronRight size={18} />
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveEvaluation}
                disabled={saving || completed}
              >
                {saving ? (
                  <>
                    <Clock size={18} className="spin" />
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Sauvegarder l'évaluation
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Quick Navigation */}
        <div style={{ marginTop: "20px", paddingTop: "20px", borderTop: "1px solid #e2e8f0" }}>
          <label style={{ fontSize: "0.875rem", fontWeight: "600", color: "#64748b", marginBottom: "12px", display: "block" }}>
            Navigation rapide
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {questions.map((q, index) => {
              const isAnswered = scores[q.id] > 0;
              const isCurrent = index === currentIndex;
              return (
                <button
                  key={q.id}
                  onClick={() => setCurrentIndex(index)}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    background: isCurrent ? "#3b82f6" : isAnswered ? "#10b981" : "#fff",
                    color: isCurrent ? "#fff" : isAnswered ? "#fff" : "#475569",
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    transition: "all 0.2s"
                  }}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="card-inner-box" style={{ marginTop: "20px" }}>
        <div className="card-box-header">
          <h3>Résumé de l'évaluation</h3>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "16px" }}>
          <div style={{ textAlign: "center", padding: "16px", background: "#f8fafc", borderRadius: "6px" }}>
            <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "4px" }}>Score total</div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1e293b" }}>
              {Object.values(scores).reduce((a, b) => a + b, 0).toFixed(1)}
            </div>
          </div>
          <div style={{ textAlign: "center", padding: "16px", background: "#f8fafc", borderRadius: "6px" }}>
            <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "4px" }}>Max possible</div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1e293b" }}>
              {questions.reduce((a, q) => a + q.max_score, 0)}
            </div>
          </div>
          <div style={{ textAlign: "center", padding: "16px", background: "#f8fafc", borderRadius: "6px" }}>
            <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "4px" }}>Pourcentage</div>
            <div style={{ 
              fontSize: "1.5rem", 
              fontWeight: "700", 
              color: getScoreColor(
                (Object.values(scores).reduce((a, b) => a + b, 0) / questions.reduce((a, q) => a + q.max_score, 0)) * 100
              )
            }}>
              {questions.reduce((a, q) => a + q.max_score, 0) > 0 
                ? ((Object.values(scores).reduce((a, b) => a + b, 0) / questions.reduce((a, q) => a + q.max_score, 0)) * 100).toFixed(0)
                : 0}%
            </div>
          </div>
          <div style={{ textAlign: "center", padding: "16px", background: "#f8fafc", borderRadius: "6px" }}>
            <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "4px" }}>Questions notées</div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1e293b" }}>
              {Object.keys(scores).filter(id => scores[id] > 0).length}/{questions.length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewMode;

